import usePlaceholder from 'articleEditor/hooks/usePlaceholder';
import {
  ActionFunctionParams,
  basicBlockActions,
  getTransformBlockActions,
  MenuOption,
} from 'articleEditor/utils/actions';
import cx from 'classnames';
import { setNodes } from 'lib-knowledge-document-structure/plate/common';
import { MyCalloutElement } from 'lib-knowledge-document-structure/plateTypes';
import { RenderElementProps } from 'lib-knowledge-document-structure/slate/react';
import { useMemo } from 'react';

import s from '../baseBlock/block.module.scss';
import SortableBlockWrapper from '../baseBlock/sortableBlockWrapper';
import calloutBlockStyles from './calloutBlock.module.scss';

type Props = RenderElementProps & {
  element: MyCalloutElement;
};

const changeVariety =
  (variety: MyCalloutElement['variety']) =>
  ({ element, editor, path }: ActionFunctionParams): void => {
    if (variety === element.variety) return;
    setNodes(editor, { variety }, { at: path });
  };

function getCalloutTypeActions(
  currentVariety: MyCalloutElement['variety'],
): ReadonlyArray<MenuOption> {
  return [
    { key: 'heading', label: 'Type' },
    {
      key: 'neutral',
      label: 'Neutral',
      isSelected: currentVariety === 'neutral' || !currentVariety,
      className: cx(
        calloutBlockStyles['menuItem'],
        calloutBlockStyles['neutral'],
      ),
      action: changeVariety('neutral'),
    },
    {
      key: 'info',
      label: 'Info',
      isSelected: currentVariety === 'info',
      className: cx(calloutBlockStyles['menuItem'], calloutBlockStyles['info']),
      action: changeVariety('info'),
    },
    {
      key: 'success',
      label: 'Success',
      isSelected: currentVariety === 'success',
      className: cx(
        calloutBlockStyles['menuItem'],
        calloutBlockStyles['success'],
      ),
      action: changeVariety('success'),
    },
    {
      key: 'warning',
      label: 'Warning',
      isSelected: currentVariety === 'warning',
      className: cx(
        calloutBlockStyles['menuItem'],
        calloutBlockStyles['warning'],
      ),
      action: changeVariety('warning'),
    },
    {
      key: 'error',
      label: 'Error',
      isSelected: currentVariety === 'error',
      className: cx(
        calloutBlockStyles['menuItem'],
        calloutBlockStyles['error'],
      ),
      action: changeVariety('error'),
    },
  ];
}

export default function CalloutBlock({
  attributes,
  children,
  element,
}: Props): JSX.Element {
  const placeholder = usePlaceholder({
    placeholder: 'Type something...',
    element,
  });

  const blockActions = useMemo(
    () => [
      ...basicBlockActions,
      ...getCalloutTypeActions(element.variety),
      ...getTransformBlockActions(element.type),
    ],
    [element.type, element.variety],
  );

  return (
    <SortableBlockWrapper
      className={s['blockCallout']}
      element={element}
      blockActions={blockActions}
    >
      <div
        {...attributes}
        className={cx(s['blockContent'], {
          [s['success']]: element.variety === 'success',
          [s['error']]: element.variety === 'error',
          [s['info']]: element.variety === 'info',
          [s['warning']]: element.variety === 'warning',
        })}
        data-placeholder={placeholder}
      >
        {children}
      </div>
    </SortableBlockWrapper>
  );
}
